import { useEffect, useState } from "react";
import { filterByTimeRange, TimeRange } from "./timeRange";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface FollowerRow {
  account: string;
  date: string;
  followersTotal: number; // daily growth
}

interface ChartDataPoint {
  date: string;
  [account: string]: string | number;
}

const LINE_COLORS = ["#008877", "#4F7CAC", "#C58B3A", "#806A9B"];

export default function FollowerGrowthChart({ refreshKey, timeRange }: { refreshKey: number, timeRange: TimeRange}) {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [accountNames, setAccountNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set());

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    fetch("/api/data/followers")
      .then((res) => {
        if (!res.ok) throw new Error(`Fehler beim Laden (Status ${res.status})`);
        return res.json();
      })
      .then((rows: FollowerRow[]) => {
        const accounts = Array.from(new Set(rows.map((r) => r.account))).sort();
        setAccountNames(accounts);

        // First: Per Account: Sort by date, then calculate sum
        const cumulativeByAccount = new Map<string, Map<string, number>>();

        for (const account of accounts) {
          const accountRows = rows
            .filter((r) => r.account === account)
            .sort((a, b) => a.date.localeCompare(b.date));

          const dateToTotal = new Map<string, number>();
          let runningTotal = 0;
          for (const row of accountRows) {
            runningTotal += row.followersTotal; // täglichen Zuwachs aufaddieren
            dateToTotal.set(row.date, runningTotal);
          }
          cumulativeByAccount.set(account, dateToTotal);
        }

        // 2. Sort by Date; every Account gets their cummulatitive sum per day
        const allDates = Array.from(new Set(rows.map((r) => r.date))).sort();
        const byDate = new Map<string, ChartDataPoint>();

        for (const date of allDates) {
          const point: ChartDataPoint = { date };
          for (const account of accounts) {
            const total = cumulativeByAccount.get(account)?.get(date);
            if (total !== undefined) {
              point[account] = total;
            }
          }
          byDate.set(date, point);
        }
        const cumulativeChartData = Array.from(byDate.values());
        const filteredForDisplay = filterByTimeRange(cumulativeChartData, timeRange);

        setChartData(filteredForDisplay);

      })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [refreshKey, timeRange]);

  if (isLoading) return <p>Lade Daten...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (chartData.length === 0) return <p>Noch keine Daten hochgeladen.</p>;

  // Click on Legend Item to toggle visibility of that metric
  const handleLegendClick = (dataKey: string) => {
    setHiddenMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        next.add(dataKey);
      }
      return next;
    });
  };

  return (
    <div>
        <p> Entwicklung der Followerzahlen der beiden Social Media Accounts </p>
        <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend 
            onClick={(entry) => handleLegendClick(entry.dataKey as string)}
                formatter={(value, entry) => {
                  const isHidden = hiddenMetrics.has(entry.dataKey as string);
                  return (
                    <span style={{ color: isHidden ? "#aaaaaa" : "#000000", cursor: "pointer" }}>
                      {value}
                    </span>
                  );
                }}
            />
            {accountNames.map((account, index) => (
            <Line
                key={account}
                type="monotone"
                dataKey={account}
                name={account}
                stroke={LINE_COLORS[index % LINE_COLORS.length]}
                strokeWidth={2}
                dot={false}
                connectNulls
                hide={hiddenMetrics.has(account)}
            />
            ))}
        </LineChart>
        </ResponsiveContainer>
    </div>
  );
}